import { Heart, Plus, Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useStore } from "../context/StoreContext";
import { formatINR, resolveMediaUrl } from "../lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import ReissueModal from "./ReissueModal";

export default function ProductCard({ product, light = false, index = 0 }) {
  const { addToCart, toggleWishlist, isWishlisted } = useStore();
  const wished = isWishlisted(product.id);
  const [reissueOpen, setReissueOpen] = useState(false);

  const stock = typeof product.stock_quantity === "number" ? product.stock_quantity : 1;
  const isSingular = product.is_singular !== false;
  const isSoldOut = stock <= 0;
  const label = isSingular ? "The Last One" : (stock > 1 ? `${stock} in stock` : null);

  const mrp = Number(product.mrp) || 0;
  const price = Number(product.price) || 0;
  const hasCompare = mrp > price && price > 0;
  const savings = hasCompare ? mrp - price : 0;
  const savingsPct = hasCompare ? Math.round((savings / mrp) * 100) : 0;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSoldOut) return;
    addToCart(product, 1);
    toast.success("Added to cart");
  };

  const handleReissue = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setReissueOpen(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08 }}
      data-testid={`product-card-${product.id}`}
      className="group relative"
    >
      <div className={`relative overflow-hidden ${light ? "bg-[#E8E3DD]" : "bg-[#141414]"} aspect-[3/4]`}>
        {/* Image is its own Link to PDP — tap never adds to cart */}
        <Link
          to={`/product/${product.id}`}
          data-testid={`product-image-link-${product.id}`}
          className="block absolute inset-0"
          aria-label={`View ${product.name}`}
        >
          {product.images?.[0] ? (
            <img
              src={resolveMediaUrl(product.images[0])}
              alt={product.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#A3A3A3] text-xs">No image</div>
          )}
          {product.images?.[1] && (
            <img
              src={resolveMediaUrl(product.images[1])}
              alt={`${product.name} alternate`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
            />
          )}
        </Link>

        {/* Sold Out badge — elegant serif, gold on ivory, top-left */}
        {isSoldOut && (
          <div
            data-testid={`sold-out-badge-${product.id}`}
            className="absolute top-3 left-3 z-10 font-serif-display italic"
            style={{
              background: "#F5E9CE",
              color: "#8B6E2C",
              padding: "12px 28px",
              fontSize: 26,
              letterSpacing: "0.05em",
              boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
            }}
          >
            Sold Out
          </div>
        )}

        {/* Savings badge — top-right corner, above wishlist heart if compare price */}
        {hasCompare && !isSoldOut && (
          <div
            data-testid={`savings-badge-${product.id}`}
            className="absolute top-3 left-3 z-10 font-ui"
            style={{
              background: "#F5D565",
              color: "#0A0A0A",
              padding: "4px 10px",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
            }}
          >
            {savingsPct}% OFF
          </div>
        )}

        {/* Wishlist */}
        <button
          data-testid={`wishlist-toggle-${product.id}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(product.id); }}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 z-10 ${
            wished
              ? "bg-[#F5D565] text-[#0A0A0A]"
              : "bg-black/40 backdrop-blur-md text-white hover:bg-[#F5D565] hover:text-[#0A0A0A]"
          }`}
          aria-label="Toggle wishlist"
        >
          <Heart strokeWidth={1.5} size={16} fill={wished ? "currentColor" : "transparent"} />
        </button>

        {/* Add / Notify — always visible, image tap never adds */}
        {isSoldOut ? (
          <button
            type="button"
            data-testid={`product-notify-cta-${product.id}`}
            aria-label="Notify when available"
            onClick={handleReissue}
            className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 h-8 font-ui transition-all hover:bg-[#F5D565] hover:text-[#0A0A0A]"
            style={{
              fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600,
              background: "#0A0A0A", color: "#F5D565", border: "1px solid #F5D565",
            }}
          >
            <Bell size={12} strokeWidth={2} /> Notify Me
          </button>
        ) : (
          <button
            type="button"
            onClick={handleAdd}
            data-testid={`quick-add-${product.id}`}
            aria-label="Add to cart"
            className="absolute bottom-3 right-3 z-10 flex items-center justify-center transition-transform duration-200 hover:scale-105 active:scale-95"
            style={{
              width: 32, height: 32, background: "#F5D565", color: "#0A0A0A",
              boxShadow: "0 4px 12px rgba(245,213,101,0.25)",
            }}
          >
            <Plus size={18} strokeWidth={2.5} />
          </button>
        )}
      </div>

      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/product/${product.id}`}>
            <h3 className={`font-serif-display text-lg md:text-xl truncate ${light ? "text-[#0A0A0A]" : "text-[#F5F5F5]"}`}>
              {product.name}
            </h3>
          </Link>
          <div className={`text-[10px] tracking-[0.25em] uppercase mt-1 ${light ? "text-[#525252]" : "text-[#A3A3A3]"}`}>
            {product.category}
          </div>
          {label && (
            <div
              data-testid={`product-card-label-${product.id}`}
              className="font-ui mt-1.5"
              style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "#F5D565", fontWeight: 600 }}
            >
              {isSoldOut ? "Currently Unavailable" : label}
            </div>
          )}
        </div>
        <div className="text-right whitespace-nowrap">
          <div className={`font-body text-sm ${light ? "text-[#0A0A0A]" : "text-[#F5F5F5]"}`}>
            {formatINR(price)}
          </div>
          {hasCompare && (
            <div
              data-testid={`product-mrp-${product.id}`}
              className={`font-body text-[11px] mt-0.5 line-through ${light ? "text-[#8b8b8b]" : "text-[#737373]"}`}
            >
              {formatINR(mrp)}
            </div>
          )}
        </div>
      </div>
      <ReissueModal product={product} open={reissueOpen} onClose={() => setReissueOpen(false)} />
    </motion.div>
  );
}
